import {
    ActionCreator,
    createAction,
    createReducer,
    EmptyActionCreator,
    getType,
    PayloadAction,
    PayloadActionCreator,
    Reducer,
    TypeConstant
} from 'typesafe-actions'
import { ID } from '../types/UtilityTypes'
import { RequestStatus } from '../responses/response'
import * as Record from 'fp-ts/Record'
import { RootActions } from '../redux/root'

export const payloadReducer = <S, T extends string>(state: S, action: PayloadAction<T, S>): S => action.payload

export const statusReducer = <R1 extends ActionCreator, R2 extends ActionCreator, R3 extends ActionCreator>(creators: {
    request: R1,
    success: R2,
    failure: R3,
}): Reducer<RequestStatus, RootActions> => createReducer<RequestStatus>(RequestStatus.INITIAL, {
    [getType(creators.request)]: () => RequestStatus.FETCHING,
    [getType(creators.success)]: () => RequestStatus.SUCCESS,
    [getType(creators.failure)]: () => RequestStatus.FAILURE,
})

export const mergeReducer = <S extends Record<PropertyKey, unknown>, T extends string>(state: S, action: PayloadAction<T, Partial<S>>): S => ({
    ...state,
    ...action.payload,
})

export const mergeKeyValueReducer = <K extends string, T, S extends Record<K, T>, A extends string>(
    state: Partial<S>, action: PayloadAction<A, { key: K, value: T }>): Partial<S> => ({
    ...state,
    [action.payload.key]: action.payload.value,
})

export const toggleReducer = <S extends Record<ID, boolean>, T extends string>(state: S, action: PayloadAction<T, ID>): S => ({
    ...state,
    [action.payload]: !state[action.payload],
})

export const createPaginatedAction = <TInit extends TypeConstant,
    TSuccess extends TypeConstant,
    TMore extends TypeConstant,
    TFailure extends TypeConstant = never,
    >(
    initType: TInit,
    moreType: TMore,
    successType: TSuccess,
    failureType: TFailure,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => <PInit, PSuccess, PMore = undefined, PFailure = void>() => ({
    init: createAction(initType)<PInit>(),
    more: createAction(moreType)<PMore>(),
    success: createAction(successType)<PSuccess>(),
    failure: createAction(failureType)<PFailure>(),
})

export const createSelectAction = <TSet extends TypeConstant,
    TToggle extends TypeConstant,
    TToggleAll extends TypeConstant>(
    setType: TSet,
    toggleType: TToggle,
    toggleAllType: TToggleAll,
): {
    set: PayloadActionCreator<TSet, ID[]>,
    toggle: PayloadActionCreator<TToggle, ID>,
    toggleAll: EmptyActionCreator<TToggleAll>,
} => ({
    set: createAction(setType)<ID[]>(),
    toggle: createAction(toggleType)<ID>(),
    toggleAll: createAction(toggleAllType)(),
})